/**
 * One row of a creator roster.
 *
 * Three shapes, one markup: a live creator who plays in place, an offline
 * creator with a clip of the title (same split, recorded instead of now), and a
 * creator with nothing to watch, where the whole card is the route to them.
 *
 * Split out of `CreatorTeamSection` when that file passed 800 lines. The three
 * branches stay in ONE component on purpose — the interaction must not change
 * between them, and two components rendering "a roster member" is how they
 * drift apart.
 */

import React from "react";
import { type RosterHighlight, type RosterFieldSpec } from "@devour/client";
import { DropFollowButton } from "./DropFollowButton";
import {
    type RosterMember,
    compact,
    platformIcon,
} from "./rosterMember";
import {resolveRosterCard, RosterLiveState} from "./rosterCard";
import {RosterFieldValues} from "./RosterFieldValues";

interface CreatorRosterCardProps {
    member: RosterMember;
    /** What the operator declared counts as on-title, from the roster payload. */
    filterGameNames?: readonly string[];
    /** The roster's declared extra fields, from the roster payload. */
    rosterFields?: readonly RosterFieldSpec[];
    /** The clip of this title by this creator, when they are off and one exists. */
    clip?: RosterHighlight;
    /** Live, on-game, and with a source we can actually embed. */
    playable: boolean;
    /** This member is the one currently in the theater. */
    isSelected: boolean;
    /** The clip currently in the theater, so a clip row can show its own state. */
    activeHighlightId?: string;
    onWatch: () => void;
    onPlayClip: (highlightId: string) => void;
    /** Signed-out click target for the per-creator follow. */
    onLoginRequired?: () => void;
    referralUserId?: string;
}

export function CreatorRosterCard({
    member: m, filterGameNames = [], rosterFields = [], clip, playable, isSelected, activeHighlightId, onWatch, onPlayClip,
    onLoginRequired, referralUserId,
}: CreatorRosterCardProps): React.ReactElement {
    /*
     * Identity, image, link target and what-is-true-right-now come from the
     * SHARED resolver — the same one HUB's rail reads. This row used to work
     * each of those out itself and HUB worked them out separately, which is
     * how the two drifted apart on off-title badging and on what the line
     * under a name says.
     *
     * The MARKUP below is untouched and stays PAGE's own. `PRODUCT_PLAN.md`
     * §2.4: sharing the resolver does not mean flattening the presentation,
     * and a visitor should see no difference from today.
     */
    const card = resolveRosterCard(m, {
        highlights: clip ? [clip] : [],
        /*
         * Without this, an OFFLINE member whose last session was another title
         * renders `Last live · <rival game>` on this publisher's roster,
         * unqualified and unbadged — `offGame` is only ever set while a member
         * is LIVE, so it cannot catch the offline case on its own.
         */
        filterGameNames,
        /* The roster's authored schema and this member's values, both off the payload. */
        fieldSpecs: rosterFields,
        fieldValues: m.fieldValues,
    });
    const href = card.href;

    /*
     * THE PERSON LEADS. Everything else trails.
     *
     * The row used to open with a 104px video thumbnail, which on an offline
     * roster — the ordinary state — was an empty grey box with OFFLINE stamped
     * across it. Five of those stacked read as five broken images, and the
     * creator's own face and name came second to them.
     *
     * So: avatar, then name, then what is true about them right now, then the
     * two controls. No thumbnail at all. A still frame from a past broadcast is
     * not worth the row's first 104 pixels, and while a creator IS live their
     * stream plays in the theater beside this rail rather than in miniature
     * here.
     */
    const identity =
    <span className="ct-card__who">
        {card.imageUrl
            ? <img className="ct-card__avatar" src={card.imageUrl} alt="" loading="lazy" />
            : <span className="ct-card__mono" aria-hidden="true">{card.initials}</span>}
        <span className="ct-card__meta">
            {/*
              * The name, and nothing under it but what they are doing.
              *
              * Every card used to carry "◆ Official · Modern Warfare 4". Being
              * IN this section is what makes a creator official — the heading
              * says "Official Creator Team" — so the badge restated the section
              * on every one of its own members, and restated the title already
              * in the hero, the heading and the URL.
              */}
            <span className="ct-card__name">{card.name}</span>
            {/* Live: what they are streaming. Offline: what the clip is. Resolved once. */}
            {card.detail && <span className="ct-card__now">{card.detail}</span>}
            {/*
              * The roster's own declared fields — a class year, a role, a
              * scholarship tier. Under the name because they qualify the
              * person rather than announce them, and absent entirely on a
              * roster that declares none.
              */}
            <RosterFieldValues fields={card.fields} className="ct-card__fields" />
        </span>
    </span>;

    /*
     * Status, then the two controls.
     *
     * Status is a statement, not a control, so it reads before the things you
     * can press. "Offline" covers OFFLINE and UNKNOWN alike on the public page:
     * the distinction is real — one means we know they are dark, the other that
     * we have no fresh observation — but "Not checked" printed on a publisher's
     * own site reads as our tooling being broken, and a visitor cannot act on
     * the difference either way. It stays in the payload
     * (`effectiveLiveState`) for admin.
     */
    const status =
    <span className="ct-card__status">
        {card.liveState === RosterLiveState.LIVE_ON_TITLE && <span className="ct-card__live">Live</span>}
        {card.liveState === RosterLiveState.LIVE_OFF_TITLE &&
            <span className="ct-card__offgame">{card.offTitleGame ?? "Other game"}</span>}
        {card.liveState === RosterLiveState.CLIP && <span className="ct-card__clip">▶ Clip</span>}
        {/*
          * OFFLINE and UNKNOWN both read "Offline" on the public page. The
          * distinction is real and the resolver keeps it — one means we know
          * they are dark, the other that we have no fresh observation — but
          * "Not checked" printed on a publisher's own site reads as our tooling
          * being broken, and a visitor cannot act on the difference either way.
          * PAGE chooses to collapse them; it no longer LOSES them.
          */}
        {(card.liveState === RosterLiveState.OFFLINE || card.liveState === RosterLiveState.UNKNOWN) &&
            <span className="ct-card__offline">Offline</span>}
        {/*
          * Viewers while live, followers otherwise. Viewer count is zero for
          * everyone who is off, so it said nothing on most of the roster most
          * of the time; followers is the figure that still means something when
          * a creator is dark.
          */}
        {m.isLive && m.viewerCount > 0 &&
            <span className="ct-card__viewers">{compact(m.viewerCount)}</span>
        }
        {!m.isLive && (m.followerCount ?? 0) > 0 &&
            <span className="ct-card__followers">{compact(m.followerCount as number)}</span>
        }
    </span>;

    const className = [
        "ct-card",
        card.liveState === RosterLiveState.LIVE_ON_TITLE ? "ct-card--live" : "",
        isSelected ? "ct-card--selected" : "",
    ].filter(Boolean).join(" ");

    /*
     * The row is NOT a link, in any of its three states.
     *
     * It used to be: a non-playable card was one big anchor, so a click anywhere
     * — the name, the empty space beside it, a mis-aimed tap — replaced the
     * publisher's page with a creator page. On a roster that is the wrong
     * instinct. Someone arrived here for the title, and the roster exists to be
     * a place you stay while you look at people from it.
     *
     * Opening a creator is now one deliberate control: the platform mark on the
     * right. Small, obvious, and impossible to hit by accident while browsing.
     */
    const aside =
    <span className="ct-card__aside">
        {/*
          * Following ONE creator, not the roster.
          *
          * The section used to carry a single "follow the whole team" bar, which
          * assumed a want people do not have — a visitor here for one creator is
          * not asking for all of them. Only for creators who have a Drop of
          * their own; there is nothing to follow otherwise.
          */}
        {m.dropSlug &&
            <DropFollowButton
                slug={m.dropSlug}
                variant="icon"
                onLoginRequired={onLoginRequired}
                referralUserId={referralUserId}
            />
        }
        {href &&
            <a
                className="ct-card__open"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${card.name}`}
                title={`Open ${card.name}`}
            >
                {platformIcon(m.platform)}
            </a>
        }
    </span>;

    /*
     * One row shape for all three states: person, status, controls.
     *
     * A live creator PLAYS in place rather than navigating — the roster's claim
     * is that it is one view of everyone playing this title across three
     * platforms, and sending someone to Twitch on the first click hands that
     * view straight back to the platform. So watching is a control in the
     * trailing group, beside follow and the platform mark, rather than a
     * thumbnail leading the row.
     *
     * The controls are separate elements rather than one big card-sized target:
     * an anchor nested in a button is invalid markup, and the browser resolves
     * it by ignoring one of them.
     */
    const watch = playable
        ? <button
            type="button"
            className="ct-card__play"
            aria-pressed={isSelected}
            aria-label={`${isSelected ? "Stop watching" : "Watch"} ${card.name}`}
            onClick={onWatch}
        >
            ▶
        </button>
        : clip
            ? <button
                type="button"
                className="ct-card__play"
                aria-pressed={clip.id === activeHighlightId}
                aria-label={`Play ${card.name}'s clip: ${clip.title}`}
                onClick={() => onPlayClip(clip.id)}
            >
                ▶
            </button>
            : null;

    return (
        <div className={className}>
            {identity}
            {status}
            {/* Its own class — `ct-card__aside` belongs to the follow/platform
                pair inside, and reusing it here nested the same class in itself
                so the row picked up its padding twice. */}
            <span className="ct-card__controls">
                {watch}
                {aside}
            </span>
        </div>
    );
}
